<template>
  <div v-if="visible" class="legal-modal">
    <div class="modal-overlay" @click.self="close">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ displayTitle }}</h3>
          <button @click="close" class="close-btn" :title="$t('legal.close')">
            ×
          </button>
        </div>
        
        <div class="modal-body">
          <div v-if="loading" class="loading">
            <div class="spinner"></div>
            <p>{{ $t('legal.loadingDocument') }}</p>
          </div>
          <div v-else-if="error" class="error">
            <p class="error-message">{{ error }}</p>
            <button @click="retry" class="btn btn-secondary">{{ $t('credits.retry') }}</button>
          </div>
          <div v-else class="document-content">
            <pre>{{ content }}</pre>
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="close" class="btn btn-primary">
            {{ $t('legal.close') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '../services/api'

export default {
  name: 'LegalDocumentModal',
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    documentType: {
      type: String,
      required: true,
      validator: (value) => ['terms', 'privacy'].includes(value)
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const title = ref('')
    const content = ref('')
    const loading = ref(false)
    const error = ref(null)

    const displayTitle = computed(() => {
      if (title.value) return title.value
      return props.documentType === 'terms' ? t('legal.termsOfService') : t('legal.privacyPolicy')
    })

    const fetchDocument = async () => {
      loading.value = true
      error.value = null
      
      try {
        let response
        if (props.documentType === 'terms') {
          response = await api.getTermsOfService()
        } else {
          response = await api.getPrivacyPolicy()
        }
        
        if (response.success) {
          content.value = response.content
          title.value = response.document || (props.documentType === 'terms' ? t('legal.termsOfService') : t('legal.privacyPolicy'))
        } else {
          error.value = response.error || t('legal.failedToLoad')
        }
      } catch (err) {
        console.error('Error fetching legal document:', err)
        error.value = t('legal.failedToLoadRetry')
      } finally {
        loading.value = false
      }
    }

    const close = () => {
      emit('close')
    }

    const retry = () => {
      fetchDocument()
    }

    // Watch for visibility changes to load content when modal opens
    watch(() => props.visible, (newVisible) => {
      if (newVisible) {
        fetchDocument()
      } else {
        // Reset content when modal closes
        content.value = ''
        title.value = ''
        error.value = null
      }
    }, { immediate: true })

    return {
      displayTitle,
      content,
      loading,
      error,
      close,
      retry
    }
  }
}
</script>

<style scoped>
.legal-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
}

.modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  animation: fadeIn 0.2s ease;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 700px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: var(--gradient-hero);
  color: white;
  flex-shrink: 0;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  min-height: 200px;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #718096;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

.error {
  text-align: center;
  padding: 2rem;
}

.error-message {
  color: #c53030;
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
}

.document-content {
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
  font-size: 0.9rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
  color: #2d3748;
}

.document-content pre {
  margin: 0;
  font-family: inherit;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.95rem;
  transition: all 0.2s ease;
}

.btn-primary {
  background: var(--gradient-hero);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(102, 126, 234, 0.3);
}

.btn-secondary {
  background: #e2e8f0;
  color: #4a5568;
}

.btn-secondary:hover {
  background: #cbd5e0;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .modal-content {
    max-width: 95%;
    max-height: 90vh;
  }
  
  .modal-header {
    padding: 1rem;
  }
  
  .modal-body {
    padding: 1rem;
  }
  
  .modal-footer {
    padding: 1rem;
  }
  
  .document-content {
    font-size: 0.8rem;
  }
}
</style>
